import { useState } from "react";
import { MdPushPin } from "react-icons/md";

import { PinnedLink } from "@megaron/docs-contracts";
import { Uuid } from "@megaron/uuid";

import { navAccent } from "../nav/navConstants";
import { useCurrentPageLink } from "./CurrentPageLinkContext";
import { NavCurrentPageRow } from "./NavCurrentPageRow";
import { NavPinnedLinkRow } from "./NavPinnedLinkRow";
import { PinnedLinkDialog } from "./PinnedLinkDialog";
import { usePinnedLinks } from "./usePinnedLinks";

type Props = {
  onNavigate?: () => void;
};

type DialogState = { uri: string; initialName: string; pinId?: Uuid };

export const NavPinnedLinks: React.FC<Props> = ({ onNavigate }) => {
  const currentPage = useCurrentPageLink();
  const { links, isLoading, isLimitReached, findByUri, savePin, isSaving, removePin, isRemoving } = usePinnedLinks();

  const [dialogState, setDialogState] = useState<DialogState | undefined>(undefined);

  const isPinActive = (pin: PinnedLink) => pin.uri === currentPage.uri;

  const currentPin = findByUri(currentPage.uri);

  const canPinCurrentPage = !isLoading && !currentPage.isPending && !currentPin;

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem", flexShrink: 0 }}>
      {canPinCurrentPage && (
        <div css={{ marginBottom: links.length > 0 ? "0.5rem" : 0 }}>
          <NavCurrentPageRow
            uri={currentPage.uri}
            name={currentPage.name}
            isLimitReached={isLimitReached}
            onPin={() => setDialogState({ uri: currentPage.uri, initialName: currentPage.name })}
          />
        </div>
      )}

      {links.length > 0 && (
        <>
          <div css={{ display: "flex", alignItems: "center", gap: "0.5rem", color: navAccent, opacity: 0.6 }}>
            <MdPushPin css={{ fontSize: "0.875rem" }} />
            <span css={{ fontSize: "0.75rem", fontWeight: 600 }}>Przypięte</span>
          </div>

          <div css={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}>
            {links.map((pin) => (
              <NavPinnedLinkRow
                key={pin.id}
                pin={pin}
                isActive={isPinActive(pin)}
                onEdit={() => setDialogState({ uri: pin.uri, initialName: pin.name, pinId: pin.id })}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </>
      )}

      {dialogState && (
        <PinnedLinkDialog
          uri={dialogState.uri}
          initialName={dialogState.initialName}
          isEditing={Boolean(dialogState.pinId)}
          isSaving={isSaving}
          isRemoving={isRemoving}
          onSave={async ({ name }) => {
            await savePin({ id: dialogState.pinId, uri: dialogState.uri, name });
          }}
          onRemove={
            dialogState.pinId
              ? async () => {
                  await removePin(dialogState.pinId as Uuid);
                }
              : undefined
          }
          onClose={() => setDialogState(undefined)}
        />
      )}
    </div>
  );
};
